import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { selectAllId, WITHOUT_DEPARTMENTS_ID } from 'pages/tasks/constants';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Skeleton } from 'primereact/skeleton';

import { DROPDOWN_FILTER_DELAY } from '../../constants';
import {
  Employee,
  ObjectType,
  TreeNode
} from '../../types/interfaces';
import { CustomDropdown } from '../../ui/custom-controls/CustomDropdown';
import TreeDropdownCheckbox from '../../ui/custom-controls/TreeDropdownCheckbox';
import { TagsComponent } from '../../ui/TagsComponent';
import '../../ui/css/EmployeeProductivityCalcDialog.css';
import { getAllKeys, hasChildren, PickerTexts } from '../../utils';
import { DepartmentsService, EmployeesService } from '../services';

function isEmployeeWithoutDepartments(e: Employee): boolean {
  const hasDeptIds = (e.departmentIds?.length ?? 0) > 0;
  const hasDeptNodes = Array.isArray(e.department) && e.department.length > 0;
  return !hasDeptIds && !hasDeptNodes;
}

function departmentIdsFromSelection(
  selectedLeaves: TreeNode[],
  treeDropDownValue: TreeNode[],
  rawDepartments: TreeNode[]
): number[] {
  const hasSelectAll = selectedLeaves.some((n) => n.key === selectAllId);
  const keys = new Set<string>();

  if (hasSelectAll) {
    getAllKeys(rawDepartments).forEach((k) => {
      if (k !== selectAllId && k !== WITHOUT_DEPARTMENTS_ID) {
        keys.add(k);
      }
    });
  } else {
    selectedLeaves.forEach((n) => {
      if (n.key !== selectAllId && n.key !== WITHOUT_DEPARTMENTS_ID) {
        keys.add(n.key);
      }
    });
  }

  return Array.from(keys)
    .map((k) => parseInt(k, 10))
    .filter((n) => !Number.isNaN(n));
}

function selectionIncludesWithoutDepartments(selectedLeaves: TreeNode[]): boolean {
  return selectedLeaves.some((n) => n.key === WITHOUT_DEPARTMENTS_ID);
}

function mergeEmployeesDedupe(...lists: Employee[][]): Employee[] {
  const map = new Map<string | number, Employee>();
  lists.flat().forEach((e) => {
    if (e.id != null) map.set(e.id, e);
  });
  return Array.from(map.values()).sort((a, b) =>
    (a.fullName || '').localeCompare(b.fullName || '', 'ru')
  );
}

function normalizedEmployeeId(employee: Employee): number | null {
  const id = Number(employee.id);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export interface EmployeeProductivityCalcDialogProps {
  visible: boolean;
  onHide: () => void;
  onCalculationSuccess?: () => void | Promise<void>;
  onCalculationError?: (detail: string) => void;
  /** Текущие строки таблицы сотрудников (без отдельного запроса для «без участков»). */
  tableEmployees: Employee[];
}

const EmployeeProductivityCalcDialog: React.FC<
  EmployeeProductivityCalcDialogProps
> = ({
  visible,
  onHide,
  onCalculationSuccess,
  onCalculationError,
  tableEmployees
}) => {
  const [treeDropDownValue, setTreeDropDownValue] = useState<TreeNode[]>([]);
  const [rawDepartments, setRawDepartments] = useState<TreeNode[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<TreeNode[]>(
    []
  );
  const [treeItemToRemove, setTreeItemToRemove] = useState<string | number>(
    ''
  );

  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesByDepartment, setEmployeesByDepartment] = useState<
    Employee[]
  >([]);
  const [excludedEmployees, setExcludedEmployees] = useState<Employee[]>([]);
  const [calculationLoading, setCalculationLoading] = useState(false);

  const dropdownValueLength = selectedDepartments.length;
  const filteredDepartmentTags = useMemo(
    () =>
      selectedDepartments.map(department => department.key === selectAllId ? {...department, label: 'Выбраны все участки'}: department).filter(
        (node) => !hasChildren(node, treeDropDownValue)
      ),
    [selectedDepartments, treeDropDownValue]
  );

  const mergedEmployees = useMemo(() => {
    const map = new Map<string | number, Employee>();
    employeesByDepartment.forEach((e) => {
      if (e.id != null) map.set(e.id, e);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.fullName || '').localeCompare(b.fullName || '', 'ru')
    );
  }, [employeesByDepartment]);

  const excludedTagsItems = useMemo<ObjectType[]>(
    () =>
      excludedEmployees.map((e) => ({
        id: e.id,
        label: e.fullName || ''
      })),
    [excludedEmployees]
  );

  const employeeIdsToCalculate = useMemo(() => {
    const excludedIds = new Set(
      excludedEmployees
        .map(normalizedEmployeeId)
        .filter((id): id is number => id !== null)
    );
    const ids = new Set<number>();

    mergedEmployees.forEach((employee) => {
      const id = normalizedEmployeeId(employee);
      if (id !== null && !excludedIds.has(id)) {
        ids.add(id);
      }
    });

    return Array.from(ids);
  }, [excludedEmployees, mergedEmployees]);

  const orphanEmployeesFromTable = useMemo(
    () => tableEmployees.filter(isEmployeeWithoutDepartments),
    [tableEmployees]
  );

  const loadDepartments = useCallback(async () => {
    setDepartmentsLoading(true);
    try {
      const result = await DepartmentsService.getDepartments();
      const data = result.data && Array.isArray(result.data) ? result.data : [];
      setRawDepartments(data);
      setTreeDropDownValue(
        data.length
          ? [
            { key: selectAllId, label: 'Выбрать все участки' },
            {
              key: WITHOUT_DEPARTMENTS_ID,
              label: 'Сотрудники без участков'
            },
            ...data
          ]
          : [
            { key: selectAllId, label: 'Выбрать все участки' },
            {
              key: WITHOUT_DEPARTMENTS_ID,
              label: 'Сотрудники без участков'
            }
          ]
      );
    } catch (e) {
      console.error(e);
      setRawDepartments([]);
      setTreeDropDownValue([
        { key: selectAllId, label: 'Выбрать все участки' },
        { key: WITHOUT_DEPARTMENTS_ID, label: 'Сотрудники без участков' }
      ]);
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    loadDepartments();
  }, [visible, loadDepartments]);

  useEffect(() => {
    if (!visible) {
      setSelectedDepartments([]);
      setTreeItemToRemove('');
      setEmployeesByDepartment([]);
      setExcludedEmployees([]);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;

    const timer = window.setTimeout(() => {
      const ids = departmentIdsFromSelection(
        selectedDepartments,
        treeDropDownValue,
        rawDepartments
      );
      const includeOrphans = selectionIncludesWithoutDepartments(
        selectedDepartments
      );

      if (ids.length === 0 && !includeOrphans) {
        if (!cancelled) setEmployeesByDepartment([]);
        return;
      }

      if (ids.length === 0 && includeOrphans) {
        if (!cancelled) {
          setEmployeesByDepartment(mergeEmployeesDedupe(orphanEmployeesFromTable));
        }
        return;
      }

      (async () => {
        if (!cancelled) setEmployeesLoading(true);
        try {
          const responses = await Promise.all(
            ids.map((id) => EmployeesService.getByDepartments(id))
          );
          if (cancelled) return;
          const fromApi: Employee[] = [];
          responses.forEach((resp) => {
            const list = resp?.data?.value;
            if (Array.isArray(list)) fromApi.push(...list);
          });
          const merged = includeOrphans
            ? mergeEmployeesDedupe(fromApi, orphanEmployeesFromTable)
            : mergeEmployeesDedupe(fromApi);
          if (!cancelled) setEmployeesByDepartment(merged);
        } catch (e) {
          if (!cancelled) {
            console.error(e);
            setEmployeesByDepartment([]);
          }
        } finally {
          if (!cancelled) setEmployeesLoading(false);
        }
      })();
    }, DROPDOWN_FILTER_DELAY);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    visible,
    selectedDepartments,
    treeDropDownValue,
    rawDepartments,
    orphanEmployeesFromTable
  ]);

  useEffect(() => {
    setExcludedEmployees((prev) => {
      if (mergedEmployees.length === 0) return [];
      return prev
        .map((ex) => {
          if (ex.id == null) return null;
          return (
            mergedEmployees.find((e) => e.id != null && e.id === ex.id) ?? null
          );
        })
        .filter((e): e is Employee => e != null);
    });
  }, [mergedEmployees]);

  const handleDeleteDepartmentTag = (key: string | number) => {
    setTreeItemToRemove(key);
  };

  const handleExcludedTagRemove = (id: string | number) => {
    setExcludedEmployees((prev) => prev.filter((e) => e.id !== id));
  };

  const assignExcludedEmployees = useCallback(
    (v: Employee | Employee[] | null) => {
      setExcludedEmployees(
        v == null ? [] : Array.isArray(v) ? v : [v]
      );
    },
    []
  );

  const exclusionDropdownLabel = useMemo(() => {
    if (mergedEmployees.length === 0 && !employeesLoading) {
      return 'Участки не выбраны или не содержат сотрудников';
    }
    if (excludedEmployees.length > 0) {
      return `Исключено из расчёта: ${excludedEmployees.length}`;
    }
    return 'Выделите сотрудников для исключения из расчёта';
  }, [mergedEmployees.length, employeesLoading, excludedEmployees.length]);

  const isEmployeeListRefreshing =
    employeesLoading && mergedEmployees.length > 0;

  const exclusionTagSkeletonCount = useMemo(() => {
    if (!isEmployeeListRefreshing) return 0;
    return Math.min(Math.max(excludedEmployees.length, 3), 10);
  }, [
    isEmployeeListRefreshing,
    excludedEmployees.length
  ]);

  const handleCalculate = async () => {
    if (employeeIdsToCalculate.length === 0) {
      onCalculationError?.(
        'Выберите сотрудников для расчета производительности'
      );
      return;
    }

    setCalculationLoading(true);
    try {
      await EmployeesService.calculateEmployeesProductivity(
        employeeIdsToCalculate
      );
      onHide();
      await onCalculationSuccess?.();
    } catch {
      onCalculationError?.(
        'Не удалось рассчитать производительность сотрудников'
      );
    } finally {
      setCalculationLoading(false);
    }
  };

  const footer = (
    <div className="productivity-calc-dialog__footer">
      <Button
        type="button"
        label="Рассчитать производительность сотрудников"
        onClick={handleCalculate}
        loading={calculationLoading}
        disabled={calculationLoading}
      />
      <Button
        type="button"
        label="Отмена"
        severity="secondary"
        onClick={onHide}
      />
    </div>
  );

  return (
    <Dialog
      className="productivity-calc-dialog"
      header="Автоматический расчет производительности сотрудников"
      visible={visible}
      onHide={onHide}
      style={{ width: 'min(720px, 96vw)',
        minWidth: '720px',
        minHeight: 'fit-content'
      }}
      modal
      draggable={false}
      footer={footer}
    >
      <div className="productivity-calc-dialog__section">
        <span className="productivity-calc-dialog__section-label">Участки</span>
        {departmentsLoading ? (
          <ProgressSpinner style={{ width: '32px', height: '32px' }} />
        ) : (
          <TreeDropdownCheckbox
            customWidth="270px"
            selected={selectedDepartments}
            value={treeDropDownValue}
            treeDropdownPlaceholder={PickerTexts.departments(
              dropdownValueLength
            )}
            onSelectionChange={setSelectedDepartments}
            toDelete={treeItemToRemove}
          />
        )}
        {!departmentsLoading && (
          <Card style={{ width: '100%', color: '#6c757d' }}>
            <p className="m-0">Выбранные участки:</p>
            <ScrollPanel
              style={{ width: '100%', height: '150px' }}
              className="employee-card-scroll-pannel"
            >
              {filteredDepartmentTags.length === 0 && (
                <p>Участки не выбраны</p>
              )}
              <TagsComponent
                initialTagsItems={filteredDepartmentTags}
                onDelete={handleDeleteDepartmentTag}
              />
            </ScrollPanel>
          </Card>
        )}
      </div>

      <div className="productivity-calc-dialog__section">
        <span className="productivity-calc-dialog__section-label">
          Исключить сотрудников из расчёта
        </span>
        {isEmployeeListRefreshing ? (
          <div className="productivity-calc-dialog__dropdown-skeleton">
            <Skeleton width="100%" height="2.625rem" borderRadius="6px" />
          </div>
        ) : employeesLoading ? (
          <ProgressSpinner style={{ width: '32px', height: '32px' }} />
        ) : mergedEmployees.length === 0 ? (
          <div className="productivity-calc-dialog__employee-picker-empty">
            Участки не выбраны или не содержат сотрудников
          </div>
        ) : (
          <CustomDropdown<Employee>
            multiplySelectMode
            assetOptions={mergedEmployees}
            selectedAsset={excludedEmployees}
            setSelectedAsset={
              assignExcludedEmployees as React.Dispatch<
                React.SetStateAction<Employee | Employee[] | null>
              >
            }
            onSelectionChange={assignExcludedEmployees}
            fieldName="fullName"
            columns={[
              {
                field: 'fullName',
                header: 'Сотрудник',
                width: '100%'
              }
            ]}
            fixedLabel={exclusionDropdownLabel}
            placeholder="Выберите сотрудников"
            emptyMessage="Нет сотрудников для отображения"
            showClearOnHover
            clearButtonTooltip="Сбросить исключения"
            customHeight={280}
            style={{ width: '100%' }}
          />
        )}
        {mergedEmployees.length > 0 && (
          <Card style={{ width: '100%', color: '#6c757d' }}>
            <p className="m-0">Исключённые из расчёта:</p>
            <ScrollPanel
              style={{ width: '100%', height: '150px' }}
              className="employee-card-scroll-pannel"
            >
              {isEmployeeListRefreshing ? (
                <div className="productivity-calc-dialog__tags-skeleton">
                  {Array.from(
                    { length: exclusionTagSkeletonCount },
                    (_, i) => (
                      <Skeleton
                        key={`exc-tag-sk-${i}`}
                        width={`${6.5 + (i % 4) * 1.25}rem`}
                        height="2rem"
                        borderRadius="2rem"
                      />
                    )
                  )}
                </div>
              ) : (
                <>
                  {excludedTagsItems.length === 0 && (
                    <p>Нет исключённых сотрудников</p>
                  )}
                  <TagsComponent
                    initialTagsItems={excludedTagsItems}
                    onDelete={handleExcludedTagRemove}
                  />
                </>
              )}
            </ScrollPanel>
          </Card>
        )}
      </div>
    </Dialog>
  );
};

export default EmployeeProductivityCalcDialog;
